import { Col, Row } from "react-bootstrap";
import { useAMCQueryData } from "./hooks/useAMCQueryData";
import { useEffect, useMemo, useRef } from "react";
import {
  BASE_PATH_TO_PURCHASE_REPORT_CONFIG,
  CANNED_QUERIES,
  DEFAULT_LOOKBACK_PERIOD_IN_DAYS,
  FEATURED_REPORTS,
} from "./utils";
import { buildTextColumn } from "views/merchandise/columns/buildTextColumn";
import { toTitleCase } from "utils/formatText";
import ExpanderContainer from "components/core/basic/ExpanderContainer";
import { useAppSelector } from "redux/hooks";
import useCustomTableFilters from "hooks/useCustomTableFilters";
import Table from "views/merchandise/tables/BulkActionsTable";
import { Link } from "react-router-dom";
import { trellisPalette } from "components/custom/analytics/palettes";
import { FaCog, FaTrash } from "react-icons/fa";
import { Button, buttonRecipe } from "components/core/basic/Button";
import { Flex } from "../../../styled-system/jsx";
import ConfirmationModal from "components/core/blocks/ConfirmationModal";
import { css } from "../../../styled-system/css";
import Switch from "react-bootstrap-switch";
import AMCReportsListing from "./AMCReportsListing";
import { AMC_REPORT_TYPE_FILTER } from "./audience/lifecycleFilters";
import useTableSettings from "hooks/useTableSettings";
import ToolTips from "utils/toolTips";

const AMCSubscriptionsTable = ({
  selectedQueryToExpand,
  setSelectedQuery,
  setShowQueryForm,
  toggleStatusHandler,
  unsubscribeHandler,
  setSelectedQueryToExpand,
}) => {
  const { marketPlace } = useAppSelector((state) => state.marketPlace);
  const TABLE_ID = `amcSubscriptionDetailsTable`;
  const tableRef = useRef();

  const additionalFilters = [AMC_REPORT_TYPE_FILTER];
  const initialFilters = selectedQueryToExpand ? [{ ...AMC_REPORT_TYPE_FILTER, value: [selectedQueryToExpand] }] : null;

  const filterProps = useCustomTableFilters(marketPlace, TABLE_ID, {});
  const { updateTableSettings: setSavedTableFilters } = useTableSettings(TABLE_ID, "tableFilters", []);

  useEffect(() => {
    if (selectedQueryToExpand) {
      setSavedTableFilters(initialFilters);
      setSelectedQueryToExpand(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedQueryToExpand]);

  const { data: subscribedQueriesData, isLoading } = useAMCQueryData();

  const tableData = useMemo(() => {
    const basePathToPurchaseReportMeta = FEATURED_REPORTS.find(
      (report) => report.name === "PATH_TO_PURCHASE_GROUPS_INTERNAL",
    );
    const basePathToPurchaseReportSpec = {
      ...basePathToPurchaseReportMeta,
      ...BASE_PATH_TO_PURCHASE_REPORT_CONFIG,
    };

    const data = [...subscribedQueriesData, basePathToPurchaseReportSpec]?.map((row) => {
      const subscriptionQuerySpec = [...CANNED_QUERIES, ...FEATURED_REPORTS].find((querySpec) => {
        return querySpec.name === row.query_type;
      });
      // Set default lookback period for legacy subscriptions before parameters could be customized
      if (subscriptionQuerySpec?.parameter_list?.includes("days") && "days" in row.parameters === false) {
        row.parameters = { ...row.parameters, days: DEFAULT_LOOKBACK_PERIOD_IN_DAYS };
      }
      return { ...subscriptionQuerySpec, ...row };
    });
    return data?.sort((a, b) => {
      return a.id - b.id;
    });
  }, [subscribedQueriesData]);

  const tableColumns = useMemo(() => {
    return [
      {
        key: "link",
        compareKey: null,
        options: {
          className: "text-start justify-content-start px-5 h-100",
          cellContainerClasses: "text-start justify-content-start",
          metric: {
            id: "link",
            format: "text",
            accessor: (d) => d,
            name: "Link",
          },
          formatter: (row) => {
            const { id, isStatic, href } = row;
            return (
              <Link
                style={{ color: trellisPalette[13], fontWeight: 600 }}
                className="fs-standard"
                to={isStatic ? href : `/user/amc/dashboard/reports/${id}`}
              >
                View Reports
              </Link>
            );
          },
          hideFilter: true,
        },
      },
      buildTextColumn("frequency", "Frequency", {
        formatter: (v) => {
          return <span>{toTitleCase(v)}</span>;
        },
        hideFilter: true,
      }),
      buildTextColumn("parameters", "Lookback Period", {
        formatter: (v) => {
          return <span>{v.days} Days</span>;
        },
        hideFilter: true,
      }),
      buildTextColumn("parameters", "Parameters", {
        width: 170,
        className: "text-start justify-content-start px-5 h-100",
        cellContainerClasses: "text-start justify-content-start",
        formatter: (parameters) => {
          return (
            <div className="text-start text-wrap">
              {Object.keys(parameters)?.map((param) => {
                switch (param) {
                  case "tag":
                    return (
                      <Flex className={css({ overflow: "wrap" })}>
                        <span className={css({ fontWeight: 600, paddingRight: "xsmall" })}>Tags: </span>
                        <span>{parameters[param].join(", ")}</span>
                      </Flex>
                    );
                  default:
                    return null;
                }
              })}
            </div>
          );
        },
        hideFilter: true,
      }),
      {
        key: "actions",
        compareKey: null,
        options: {
          className: "text-start justify-content-start px-5 h-100",
          cellContainerClasses: "text-start justify-content-start",
          metric: {
            id: "actions",
            format: "text",
            accessor: (d) => d,
            name: "Actions",
            width: 30,
          },

          formatter: (subscription) => {
            const { id, display_name, isStatic } = subscription;
            if (isStatic) {
              return (
                <Flex alignItems="center" gap="large" justifyContent={"flex-end"}>
                  <ToolTips
                    toolTip="Actions are unavailable for the base version of featured reports."
                    toolTipId="actionsUnavailableAMCFeaturedReport"
                    direction="top"
                  />
                </Flex>
              );
            }
            return (
              <Flex alignItems="center" gap="large" justifyContent={"flex-end"}>
                <Button
                  variant={"contentOnly"}
                  onPress={() => {
                    setSelectedQuery({ ...subscription, subscribed: true });
                    setShowQueryForm(true);
                  }}
                >
                  <FaCog className={css({ color: "#777", fontSize: "large" })} />
                </Button>

                <ConfirmationModal
                  title={`Confirm Unsubscription`}
                  confirmButtonText="Confirm"
                  confirmationMessage={
                    <p className="fs-4 w-75 mx-auto pt-0 pb-4">
                      Are you sure you want to unsubscribe from the {display_name} query? If you continue, reports will
                      no longer be generated for this query effective immediately.
                    </p>
                  }
                  onConfirm={() => {
                    unsubscribeHandler.mutate(id);
                  }}
                  inlineModal={true}
                  inlineButtonContainerStyles={{}}
                  modalButtonText={
                    <button className={buttonRecipe({ variant: "contentOnly" })}>
                      <FaTrash className={css({ color: "#777", fontSize: "large" })} />
                    </button>
                  }
                />
                <div style={{ transform: "scale(.75)" }} className="pt-2">
                  <Switch
                    onText=""
                    offText=""
                    value={subscription.enabled}
                    onColor="#FFFFFF"
                    onChange={(e) => {
                      toggleStatusHandler.mutate({
                        subscriptionId: id,
                        enabled: e.state.value,
                        queryName: display_name,
                      });
                    }}
                  />
                </div>
              </Flex>
            );
          },
        },
      },
    ];
  }, [setSelectedQuery, setShowQueryForm, toggleStatusHandler, unsubscribeHandler]);

  return (
    <Row className="pb-5">
      <Col xs={12} lg={{ span: 8, offset: 2 }}>
        <h3 className="pb-3 fs-2 my-0" style={{ fontWeight: 600 }}>
          Subscriptions
        </h3>
        <Table
          marketPlace={marketPlace}
          tableRef={tableRef}
          tableId={TABLE_ID}
          excludedFilterColumns={["name", "link", "parameters", "frequency", "actions"]}
          filterWidget={true}
          {...filterProps}
          data={tableData}
          getSearchCriteria={(row) => {
            const { display_name } = row ?? {};

            return `${display_name}`;
          }}
          hideBottomPagination={true}
          hideFilterToggleIcon={true}
          additionalFilterOptions={additionalFilters}
          columnSpecs={tableColumns}
          tableRowDescription={"Subscriptions"}
          defaultSorted={[{ id: "id", desc: true }]}
          isLoading={isLoading}
          handleBulkChanges={() => {}}
          minRows={0}
          defaultPageSize={5}
          isSelectable={false}
          selectionKey="id"
          isExpandable={true}
          ExpandedContent={({ row }) => {
            return (
              <ExpanderContainer>
                <AMCReportsListing fullPage={false} overrideId={row.original.id} />
              </ExpanderContainer>
            );
          }}
          titleCol={{
            id: "name",
            isStatic: true,
            Header: "Report Type",
            accessor: "text",
            format: "text",
            style: { width: "300px" },
            width: 300,
            Cell: (row) => {
              const { display_name: displayName } = row.original ?? {};
              return (
                <span
                  style={{
                    fontWeight: 500,
                  }}
                >
                  {displayName}
                </span>
              );
            },
            Filter: () => null,
          }}
        />
      </Col>
    </Row>
  );
};

export default AMCSubscriptionsTable;
